import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Eye,
  FileSpreadsheet,
  Filter,
  ListFilter,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { cn } from '@/shared/lib/cn';
import { currencyFromSp, currencyToSp, formatMoney, parseMoney } from '@/shared/lib/money';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { inRange, parseAr, parseIso } from '@/features/ventas/lib/dates';
import { DatePicker } from '@/shared/components/ui/date-picker';
import { Label } from '@/shared/components/ui/label';
import {
  eliminarCompra,
  getCompraDetalle,
  listCompras,
  type CompraDetailResponse,
  type CompraListItem,
} from './api';
import { saldoRestante, totalesRestantePorMoneda } from './lib/saldo';
import { CajaNombre } from '@/features/tesoreria/components/CajaNombre';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';

/**
 * Stage 1 of the Compras port. Lists `05.Compras`, shows enriched
 * aggregates (Retenciones + Monto Restante) and soft-deletes via the
 * eliminar cascade.
 *
 * Per-row actions:
 *   - Eye (Ver)        → navigates to read-only VerCompraPage
 *   - Pencil (Editar)  → deferred to Stage 4
 *   - Receipt          → opens Historial de Pagos dialog
 *   - Trash (Eliminar) → opens anular confirm dialog
 *
 * Shell follows the existing Contactos/Administración pattern:
 * outer `flex h-full flex-col`, sticky-header table in a
 * `min-h-0 flex-1 overflow-y-auto` scroller, footer totals fixed.
 */

/**
 * Currency labels follow the SharePoint-style strings stored on `Moneda_CO`.
 * AR has 'Pesos Argentinos', MX has 'Pesos Mexicanos'; both share 'Dolares'.
 * The active country pack drives which "home" peso label the UI shows.
 */
type MonedaCO = 'Pesos Argentinos' | 'Pesos Mexicanos' | 'Dolares';

type FilterValue = {
  status: Set<StatusCO>;
  moneda: Set<MonedaCO>;
  /** HTML date-input values (`yyyy-mm-dd`), empty string = no bound. */
  desde: string;
  hasta: string;
};

type StatusCO = 'Pendiente' | 'Pago Parcial' | 'Pagado' | 'Anulado';

const STATUS_OPTIONS: StatusCO[] = ['Pendiente', 'Pago Parcial', 'Pagado'];

/**
 * Build the landing-state filter for an active country. Defaults match
 * what an admin actually wants on landing: only the still-open
 * comprobantes, scoped to the country's home currency + USD. Pagado is
 * opt-in via the filter popover — same pattern PowerApps uses.
 */
function buildDefaultFilter(homeMonedaSp: 'Pesos Argentinos' | 'Pesos Mexicanos'): FilterValue {
  return {
    status: new Set(['Pendiente', 'Pago Parcial']),
    moneda: new Set([homeMonedaSp, 'Dolares']),
    desde: '',
    hasta: '',
  };
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'eliminar'; row: CompraListItem }
  | { kind: 'historial'; row: CompraListItem };

/**
 * Download the (search/filter-filtered) compras list as a real .xlsx — the
 * client asked to mirror the Contactos/Ventas export here. Money columns
 * are emitted as numbers (not formatted strings) so Excel can sum them.
 * Monto Restante uses the same formula as the on-screen column. Already
 * country-scoped: `listCompras` only returns the active country's rows.
 */
async function exportComprasToXlsx(rows: CompraListItem[]): Promise<void> {
  const headers = [
    'Estado',
    'Fecha',
    'Proveedor',
    'Importación',
    'Comprobante',
    'N° Comprobante',
    'Moneda',
    'Importe Bruto',
    'Impuestos',
    'Total',
    'Retenciones',
    'Pagado',
    'Monto Restante',
  ];
  const data = rows.map((r) => {
    const f = r.fields;
    const total = parseMoney(f.Total_CO) ?? 0;
    const restante = saldoRestante(r);
    return [
      f.Status_CO ?? '',
      f.FechaComprobante_CO ?? '',
      f.Proveedor_CO ?? '',
      r.importacion
        ? `N° ${r.importacion.numero}${r.importacion.descripcion ? ` - ${r.importacion.descripcion}` : ''}`
        : '',
      f.Comprobante_CO ?? '',
      f.NumeroComprobante_CO ?? '',
      f.Moneda_CO ?? '',
      parseMoney(f.TotalBruto_CO) ?? 0,
      parseMoney(f.TotalImpuestos_CO) ?? 0,
      total,
      r.importeRetenciones,
      r.importePagado,
      restante,
    ];
  });
  const today = fechaArchivoHoy();
  await downloadXlsx({
    filename: `compras_${today}.xlsx`,
    sheetName: 'Compras',
    headers,
    rows: data,
  });
}

/** Chip "Imp. N° X" junto al comprobante; la descripción va en el tooltip. */
function ImportacionChip({ importacion }: { importacion: CompraListItem['importacion'] }) {
  if (!importacion) return null;
  return (
    <span
      className="inline-flex w-fit items-center rounded border border-sky-300 bg-sky-50 px-1 text-[10px] font-semibold leading-4 text-sky-800"
      title={importacion.descripcion || undefined}
    >
      Imp. N° {importacion.numero}
    </span>
  );
}

export function ComprasPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isReadOnly = useIsReadOnly();
  const { config: activeCountry } = useActiveCountry();
  // Active country drives the home-currency filter chip and the footer
  // labels. AR sees Pesos Argentinos + Dólares; MX sees Pesos Mexicanos +
  // Dólares. The list query itself is country-scoped server-side, so
  // changing country never blends rows.
  const homeMonedaSp = currencyToSp(activeCountry.currency.primary) as
    | 'Pesos Argentinos'
    | 'Pesos Mexicanos';
  const defaultFilter = useMemo(
    () => buildDefaultFilter(homeMonedaSp),
    [homeMonedaSp],
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterValue>(defaultFilter);
  // Reset filter when the country switches — otherwise the previous
  // country's "Pesos Argentinos" chip stays selected in MX (filtering
  // out every MX row) or vice versa.
  useEffect(() => {
    setFilter(defaultFilter);
  }, [defaultFilter]);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  // Multi-user gallery — refetch on tab refocus + every 15s so two
  // admins paying compras concurrently see each other's progress.
  // Paired with the OCC guard in `POST /api/administracion/pagar`.
  const { data: rows = [], isPending, isFetching, isError, refetch } = useQuery({
    // Country is in the key so a fast AR↔MX toggle can't serve the
    // previous country's purchase list while the new fetch resolves.
    queryKey: ['compras', activeCountry.code] as const,
    queryFn: listCompras,
    staleTime: 30_000,
    // Respaldo: Realtime (abajo) es quien refresca; cada vuelta baja todas las compras.
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  // Live updates on the purchase header table — admins paying invoices
  // and the gallery viewer see each other's status flips immediately.
  useRealtimeInvalidate('purchase_headers', ['compras']);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const desde = parseIso(filter.desde);
    const hasta = parseIso(filter.hasta);
    return rows.filter((r) => {
      const f = r.fields;
      const status = (f.Status_CO ?? '') as StatusCO;
      if (status === 'Anulado') return false; // Stage 1 hides anulados; Stage 2 may add a toggle.
      if (!filter.status.has(status)) return false;
      const moneda = f.Moneda_CO ?? '';
      if (moneda && !filter.moneda.has(moneda as MonedaCO)) {
        return false;
      }
      if (desde || hasta) {
        const fecha = parseAr(f.FechaComprobante_CO);
        // When the row has no/invalid fecha, drop it from a bounded filter —
        // the user's bound is meaningful and an unparseable row can't satisfy it.
        if (!fecha) return false;
        if (!inRange(fecha, desde, hasta)) return false;
      }
      if (q) {
        const hay =
          (f.Proveedor_CO ?? '').toLowerCase().includes(q) ||
          (f.NumeroComprobante_CO ?? '').toLowerCase().includes(q) ||
          (f.Comprobante_CO ?? '').toLowerCase().includes(q) ||
          (r.importacion != null && `imp. n° ${r.importacion.numero}`.includes(q));
        if (!hay) return false;
      }
      return true;
    });
  }, [rows, search, filter]);

  // Footer totals: suma el MONTO RESTANTE por moneda, que es la misma columna
  // que muestra la tabla. Antes sumaba `Total_CO`: con el filtro por defecto
  // (Pendiente + Pago Parcial) eso miente por construcción — una compra con
  // pago parcial aportaba el total facturado y no lo que falta pagar, así que
  // el pie decía "US$ 118.444,87" cuando la deuda real era US$ 67.106,37.
  //
  // Misma función que el pie de Cuentas por Pagar, que ya lo hacía bien.
  // `pesos` es la moneda local del país activo — el listado viene scopeado por
  // país, así que "todo lo que no es Dólares" es local sin ambigüedad.
  const totals = useMemo(() => totalesRestantePorMoneda(filtered), [filtered]);

  const filterActive =
    filter.desde !== '' ||
    filter.hasta !== '' ||
    !setsEqual(filter.status, defaultFilter.status) ||
    !setsEqual(filter.moneda, defaultFilter.moneda);

  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['compras'] });
  };

  return (
    <div className="flex h-full flex-col gap-6">
      {/* Toolbar — DOM order matches desktop layout (Title, Add, Search,
          Filter, Refresh). On mobile we re-arrange via CSS `order` so the
          title row gets [Title | Refresh | Add] and search + filter
          drop onto a second row via the `basis-full` break. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          <span className="sm:hidden">Compras</span>
          <span className="hidden sm:inline">Comprobantes de Compra</span>
        </h1>
        {!isReadOnly && (
          <Button
            onClick={() => navigate('/compras/nueva')}
            className="order-2 h-12 w-12 shrink-0 gap-2 bg-foreground p-0 text-background hover:bg-foreground/90 sm:order-none sm:w-auto sm:px-4"
            aria-label="Registrar compra"
            title="Registrar compra"
          >
            <span className="hidden sm:inline">Registrar compra</span>
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        )}
        {/* Forces search + filter onto a new row on mobile only. */}
        <div className="order-3 basis-full sm:hidden" aria-hidden />
        <div className="relative order-4 min-w-[10rem] flex-1 sm:order-none sm:w-60 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        <div className="order-5 sm:order-none">
          <FiltrarPopover
            value={filter}
            onChange={setFilter}
            onReset={() => setFilter(defaultFilter)}
            active={filterActive}
            homeMonedaSp={homeMonedaSp}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={handleRefresh}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => void exportComprasToXlsx(filtered)}
          disabled={filtered.length === 0}
          aria-label="Descargar base en Excel"
          title="Descargar base en Excel"
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <ComprobantesTable
        rows={filtered}
        loading={isPending}
        error={isError}
        searchActive={Boolean(search.trim()) || filterActive}
        showRetenciones={activeCountry.features.has('retenciones')}
        onEliminar={(row) => setDialog({ kind: 'eliminar', row })}
        onVer={(row) => navigate(`/compras/${row.id}`)}
        onEditar={(row) => navigate(`/compras/${row.id}/editar`)}
        onHistorial={(row) => setDialog({ kind: 'historial', row })}
        onRetry={() => refetch()}
      />

      <FooterTotals
        pesos={totals.pesos}
        dolares={totals.dolares}
        homeCurrency={activeCountry.currency.primary}
      />

      {dialog.kind === 'eliminar' && (
        <EliminarCompraDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'historial' && (
        <HistorialPagosDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function ComprobantesTable({
  rows,
  loading,
  error,
  searchActive,
  showRetenciones,
  onEliminar,
  onVer,
  onEditar,
  onHistorial,
  onRetry,
}: {
  rows: CompraListItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  showRetenciones: boolean;
  onEliminar: (row: CompraListItem) => void;
  onVer: (row: CompraListItem) => void;
  onEditar: (row: CompraListItem) => void;
  onHistorial: (row: CompraListItem) => void;
  onRetry: () => void;
}) {
  const colCount = showRetenciones ? 9 : 8;
  const emptyMessage = searchActive
    ? 'Sin coincidencias para los filtros aplicados.'
    : 'Todavía no hay compras registradas.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Estados</Th>
              <Th>Fecha</Th>
              <Th>Proveedor</Th>
              <Th className="text-right">Importe Bruto</Th>
              <Th className="text-right">Impuestos</Th>
              <Th className="text-right">Total</Th>
              {showRetenciones && <Th className="text-right">Retenciones</Th>}
              <Th className="text-right">Monto Restante</Th>
              <Th className="w-40 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={colCount} />
            ) : error ? (
              <RowStatus cols={colCount}>
                <span className="text-destructive">
                  No se pudieron cargar las compras.
                </span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={colCount}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => (
                <ComprobanteRow
                  key={r.id}
                  row={r}
                  showRetenciones={showRetenciones}
                  onEliminar={onEliminar}
                  onVer={onVer}
                  onEditar={onEditar}
                  onHistorial={onHistorial}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudieron cargar las compras.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <ComprobanteCard
                key={r.id}
                row={r}
                onEliminar={onEliminar}
                onVer={onVer}
                onEditar={onEditar}
                onHistorial={onHistorial}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ComprobanteCard({
  row,
  onEliminar,
  onVer,
  onEditar,
  onHistorial,
}: {
  row: CompraListItem;
  onEliminar: (r: CompraListItem) => void;
  onVer: (r: CompraListItem) => void;
  onEditar: (r: CompraListItem) => void;
  onHistorial: (r: CompraListItem) => void;
}) {
  const isReadOnly = useIsReadOnly();
  const f = row.fields;
  const moneda = currencyFromSp(f.Moneda_CO);
  const total = parseMoney(f.Total_CO) ?? 0;
  const restante = saldoRestante(row);
  const isAnulated = f.Status_CO === 'Anulado';
  const isPagado = f.Status_CO === 'Pagado';
  const hasPagos = row.importePagado > 0;
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="max-w-full truncate text-sm font-medium text-foreground">
            {f.Proveedor_CO || '—'}
          </span>
          <ImportacionChip importacion={row.importacion} />
        </div>
        <EstadoPill status={(f.Status_CO ?? '') as StatusCO} />
      </div>
      <div className="text-xs text-muted-foreground">
        {f.FechaComprobante_CO || '—'} · {abbreviateComprobante(f.Comprobante_CO)} {f.NumeroComprobante_CO ?? ''}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Total
          </div>
          <div className="font-semibold text-foreground">
            {formatMoney(total, moneda)}{' '}
            <span className="text-xs font-normal text-muted-foreground">{moneda}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Restante
          </div>
          <div
            className={cn(
              'font-semibold',
              restante > 0.01 ? 'text-amber-700' : 'text-emerald-700',
            )}
          >
            {formatMoney(restante, moneda)}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1">
        <IconAction onClick={() => onVer(row)} title="Ver detalle" tone="neutral">
          <Eye className="h-4 w-4" aria-hidden />
        </IconAction>
        {!isReadOnly && (
          <IconAction
            onClick={() => onEditar(row)}
            disabled={isAnulated || isPagado}
            title={
              isAnulated
                ? 'La compra está anulada'
                : isPagado
                  ? 'No se puede editar una compra pagada'
                  : 'Editar'
            }
            tone="neutral"
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </IconAction>
        )}
        <IconAction
          onClick={() => onHistorial(row)}
          disabled={!hasPagos}
          title={hasPagos ? 'Historial de pagos' : 'Sin pagos registrados'}
          tone="neutral"
        >
          <Receipt className="h-4 w-4" aria-hidden />
        </IconAction>
        {!isReadOnly && (
          <IconAction
            onClick={() => onEliminar(row)}
            disabled={isAnulated || isPagado}
            title={
              isAnulated
                ? 'La compra ya está anulada'
                : isPagado
                  ? 'Anulá los pagos desde Tesorería antes'
                  : 'Anular'
            }
            tone="destructive"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </IconAction>
        )}
      </div>
    </li>
  );
}

function ComprobanteRow({
  row,
  showRetenciones,
  onEliminar,
  onVer,
  onEditar,
  onHistorial,
}: {
  row: CompraListItem;
  showRetenciones: boolean;
  onEliminar: (r: CompraListItem) => void;
  onVer: (r: CompraListItem) => void;
  onEditar: (r: CompraListItem) => void;
  onHistorial: (r: CompraListItem) => void;
}) {
  const isReadOnly = useIsReadOnly();
  const f = row.fields;
  const moneda = currencyFromSp(f.Moneda_CO);
  const bruto = parseMoney(f.TotalBruto_CO) ?? 0;
  const impuestos = parseMoney(f.TotalImpuestos_CO) ?? 0;
  const total = parseMoney(f.Total_CO) ?? 0;
  const restante = saldoRestante(row);
  const isAnulated = f.Status_CO === 'Anulado';
  const isPagado = f.Status_CO === 'Pagado';
  const hasPagos = row.importePagado > 0;
  return (
    <tr className="transition-colors hover:bg-muted/30">
      <Td>
        <EstadoPill status={(f.Status_CO ?? '') as StatusCO} />
      </Td>
      <Td className="text-muted-foreground">{f.FechaComprobante_CO || '—'}</Td>
      <Td>
        <div className="flex flex-col leading-tight">
          <span className="font-medium text-foreground">{f.Proveedor_CO || '—'}</span>
          {/* El chip de importación va en la línea del comprobante: la fila queda de
              dos líneas como las demás. */}
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {f.LlevaFactura_CO === 'SI'
              ? f.Comprobante_CO || '—'
              : 'No lleva Factura'}
            <ImportacionChip importacion={row.importacion} />
          </span>
        </div>
      </Td>
      <Td className="text-right text-foreground">
        <MoneyWithCode value={bruto} moneda={moneda} />
      </Td>
      <Td className="text-right text-muted-foreground">
        <MoneyWithCode value={impuestos} moneda={moneda} />
      </Td>
      <Td className="text-right font-semibold text-foreground">
        <MoneyWithCode value={total} moneda={moneda} />
      </Td>
      {showRetenciones && (
        <Td className="text-right text-muted-foreground">
          {row.importeRetenciones > 0 ? formatMoney(row.importeRetenciones, moneda) : '—'}
        </Td>
      )}
      <Td
        className={cn(
          'text-right font-semibold',
          restante > 0.01 ? 'text-amber-700' : 'text-emerald-700',
        )}
      >
        {formatMoney(restante, moneda)}
      </Td>
      <Td className="text-right">
        <div className="inline-flex items-center gap-1">
          <IconAction
            onClick={() => onVer(row)}
            title="Ver detalle"
            tone="neutral"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </IconAction>
          {!isReadOnly && (
            <IconAction
              onClick={() => onEditar(row)}
              disabled={isAnulated || isPagado}
              title={
                isAnulated
                  ? 'La compra está anulada'
                  : isPagado
                    ? 'No se puede editar una compra pagada'
                    : 'Editar'
              }
              tone="neutral"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </IconAction>
          )}
          <IconAction
            onClick={() => onHistorial(row)}
            disabled={!hasPagos}
            title={hasPagos ? 'Historial de pagos' : 'Sin pagos registrados'}
            tone="neutral"
          >
            <Receipt className="h-4 w-4" aria-hidden />
          </IconAction>
          {!isReadOnly && (
            <IconAction
              onClick={() => onEliminar(row)}
              title={
                isAnulated
                  ? 'La compra ya está anulada'
                  : isPagado
                    ? 'Anulá los pagos desde Tesorería antes'
                    : 'Anular'
              }
              tone="destructive"
              disabled={isAnulated || isPagado}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </IconAction>
          )}
        </div>
      </Td>
    </tr>
  );
}

function EstadoPill({ status }: { status: StatusCO | string }) {
  const s = status as StatusCO;
  const cls =
    s === 'Pagado'
      ? 'bg-emerald-100 text-emerald-700'
      : s === 'Pago Parcial'
        ? 'bg-indigo-100 text-indigo-700'
        : s === 'Pendiente'
          ? 'bg-amber-100 text-amber-700'
          : s === 'Anulado'
            ? 'bg-slate-200 text-slate-600'
            : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {status || '—'}
    </span>
  );
}

/**
 * Strip the redundant "US" from `formatMoney`'s "US$" prefix and rely
 * on the trailing currency code (ARS / USD) for disambiguation. Both
 * monedas end up reading as `$ N CODE`, which lines up cleanly across
 * mixed-currency rows.
 */
function MoneyWithCode({ value, moneda }: { value: number; moneda: 'ARS' | 'MXN' | 'USD' }) {
  const formatted = formatMoney(value, moneda).replace(/^US\$/, '$');
  return (
    <span className="whitespace-nowrap">
      {formatted}{' '}
      <span className="text-xs font-medium text-muted-foreground">{moneda}</span>
    </span>
  );
}

function abbreviateComprobante(raw: string | null | undefined): string {
  if (!raw) return '—';
  // Legacy PowerApps values (Factura, Orden de Compra, Tique, etc.) are
  // kept so historical rows still abbreviate cleanly even though the
  // dropdown no longer offers them.
  const map: Record<string, string> = {
    'Factura A': 'FA',
    'Factura B': 'FB',
    'Factura C': 'FC',
    Factura: 'FC',
    'Nota de Débito': 'ND',
    'Nota de Crédito': 'NC',
    'Orden de Compra': 'OC',
    Recibo: 'RC',
    Tique: 'TQ',
    'Otros Comprobantes': 'OT',
    'Extracto Bancario': 'EB',
    'Despacho de Importación': 'DI',
  };
  return map[raw] ?? raw;
}

// ---------------------------------------------------------------------------
// Filter popover
// ---------------------------------------------------------------------------

function FiltrarPopover({
  value,
  onChange,
  onReset,
  active,
  homeMonedaSp,
}: {
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  onReset: () => void;
  /** Active country's home-currency SP label — drives the Moneda chip. */
  homeMonedaSp: 'Pesos Argentinos' | 'Pesos Mexicanos';
  active: boolean;
}) {
  const [open, setOpen] = useState(false);

  const toggleStatus = (s: StatusCO) => {
    const next = new Set(value.status);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    onChange({ ...value, status: next });
  };
  const toggleMoneda = (m: MonedaCO) => {
    const next = new Set(value.moneda);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    onChange({ ...value, moneda: next });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-12 gap-2', active && 'border-foreground/60')}
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Fecha
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Desde</Label>
              <DatePicker
                value={value.desde}
                onChange={(next) => onChange({ ...value, desde: next })}
                placeholder="dd/mm/yyyy"
                max={value.hasta || undefined}
                ariaLabel="Fecha desde"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Hasta</Label>
              <DatePicker
                value={value.hasta}
                onChange={(next) => onChange({ ...value, hasta: next })}
                placeholder="dd/mm/yyyy"
                min={value.desde || undefined}
                ariaLabel="Fecha hasta"
              />
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Estado
          </p>
          <div className="flex flex-col gap-1">
            {STATUS_OPTIONS.map((s) => (
              <FilterChip
                key={s}
                label={s}
                checked={value.status.has(s)}
                onToggle={() => toggleStatus(s)}
              />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Moneda
          </p>
          <div className="flex flex-col gap-1">
            {/* Active country's home currency chip — AR: Pesos Argentinos,
                MX: Pesos Mexicanos. The other country's chip is hidden
                because rows are server-scoped by country and would never
                match. */}
            <FilterChip
              label={homeMonedaSp}
              checked={value.moneda.has(homeMonedaSp)}
              onToggle={() => toggleMoneda(homeMonedaSp)}
            />
            <FilterChip
              label="Dólares"
              checked={value.moneda.has('Dolares')}
              onToggle={() => toggleMoneda('Dolares')}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterChip({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span>{label}</span>
      {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterTotals({
  pesos,
  dolares,
  homeCurrency,
}: {
  pesos: number;
  dolares: number;
  /** Active country's home currency — drives the peso pill's label + format. */
  homeCurrency: 'ARS' | 'MXN';
}) {
  // "Pesos" works for both AR ("Pesos Argentinos") and MX ("Pesos
  // Mexicanos"); the currency code distinguishes them on mobile and via
  // the $ symbol locale in `formatMoney`.
  const pesoFullLabel =
    homeCurrency === 'MXN' ? 'Restante Pesos Mex.' : 'Restante Pesos';
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-2xl border bg-card px-3 py-3 sm:justify-end sm:px-4">
      <span className="inline-flex h-9 items-center gap-2 rounded-lg border bg-secondary px-3 text-xs">
        <span className="text-muted-foreground">
          <span className="sm:hidden">Rest. {homeCurrency}</span>
          <span className="hidden sm:inline">{pesoFullLabel}</span>
        </span>
        <span className="font-semibold text-foreground">{formatMoney(pesos, homeCurrency)}</span>
      </span>
      <span className="inline-flex h-9 items-center gap-2 rounded-lg border bg-secondary px-3 text-xs">
        <span className="text-muted-foreground">
          <span className="sm:hidden">Rest. USD</span>
          <span className="hidden sm:inline">Restante Dólares</span>
        </span>
        <span className="font-semibold text-foreground">{formatMoney(dolares, 'USD')}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Eliminar dialog
// ---------------------------------------------------------------------------

function EliminarCompraDialog({
  row,
  onClose,
}: {
  row: CompraListItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const f = row.fields;
  const moneda = currencyFromSp(f.Moneda_CO);
  const total = parseMoney(f.Total_CO) ?? 0;

  const mutation = useMutation({
    mutationFn: () => eliminarCompra(row.id),
    onSuccess: (r) => {
      if (r.outcome === 'anulated') {
        toast.success('Compra anulada', {
          description: `${r.pagosAnulados} pago(s), ${r.instrumentosAnulados} instrumento(s) y ${r.retencionesAnuladas} retención(es) también fueron anuladas.`,
        });
      } else {
        toast.info('No se pudo anular', { description: r.message });
      }
      qc.invalidateQueries({ queryKey: ['compras'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo anular la compra', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Anulando compra…'}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Anular compra</DialogTitle>
              <DialogDescription>
                Se anulará el comprobante {f.Comprobante_CO ?? ''}{' '}
                {f.NumeroComprobante_CO ? `Nº ${f.NumeroComprobante_CO}` : `#${row.id}`} de{' '}
                {f.Proveedor_CO || 'proveedor'} por {formatMoney(total, moneda)}, junto con
                sus pagos, instrumentos y retenciones asociadas.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Los movimientos de caja se anulan automáticamente. Si la compra impactaba stock,
            revisá la recepción asociada y ajustá <strong>Por Ingresar</strong> /{' '}
            <strong>Disponible</strong> manualmente desde el módulo de Inventario.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-rose-600 text-white hover:bg-rose-700"
          >
            Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Historial de Pagos dialog
// ---------------------------------------------------------------------------

function HistorialPagosDialog({
  row,
  onClose,
}: {
  row: CompraListItem;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { config: activeCountry } = useActiveCountry();
  // Hide the Retenciones column entirely on countries without the
  // retenciones feature (MX). The backend never creates retención rows
  // there, so the column would only ever read "$0,00 ARS" — which is
  // visually misleading.
  const showRetenciones = activeCountry.features.has('retenciones');
  const f = row.fields;
  const moneda = currencyFromSp(f.Moneda_CO);
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['compras', 'detalle', row.id],
    queryFn: () => getCompraDetalle(row.id),
    staleTime: 15_000,
  });

  const pagos = (q.data?.pagos ?? []).filter((p) => p.fields.Status_PC !== 'Anulado');
  const ncs = q.data?.notasCreditoPendientes ?? [];
  const hasAnyRows = pagos.length > 0 || ncs.length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Historial de Pagos</DialogTitle>
          <DialogDescription>
            {f.Comprobante_CO ?? ''}{' '}
            {f.NumeroComprobante_CO ? `Nº ${f.NumeroComprobante_CO}` : `#${row.id}`} ·{' '}
            {f.Proveedor_CO || 'proveedor'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {q.isPending ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : q.isError ? (
            <p className="py-6 text-center text-sm text-destructive">
              No se pudo cargar el historial.
            </p>
          ) : !hasAnyRows ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Todavía no se registraron pagos.
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <Th>Fecha</Th>
                    <Th>Descripción</Th>
                    <Th className="text-right">Monto Pagado</Th>
                    {showRetenciones && <Th className="text-right">Retenciones</Th>}
                    <Th className="w-16 text-right">Acciones</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pagos.map((p) => {
                    const pagoMoneda = p.fields.Moneda_PC === 'Dolares' ? 'USD' : moneda;
                    const montoPagado =
                      parseMoney(p.fields.ImporteInstrumentosPago_PC) ??
                      parseMoney(p.fields.Importe_PC) ??
                      0;
                    const retenido = parseMoney(p.fields.ImporteRetenciones_PC) ?? 0;
                    const isOpen = expanded === p.id;
                    return (
                      <PagoHistorialRow
                        key={p.id}
                        pago={p}
                        detail={q.data!}
                        pagoMoneda={pagoMoneda}
                        montoPagado={montoPagado}
                        retenido={retenido}
                        showRetenciones={showRetenciones}
                        isOpen={isOpen}
                        onToggle={() => setExpanded(isOpen ? null : p.id)}
                      />
                    );
                  })}
                  {ncs.map((nc) => (
                    <NCHistorialRow
                      key={`nc-${nc.id}`}
                      nc={nc}
                      moneda={moneda}
                      onVer={() => {
                        onClose();
                        navigate(`/compras/${nc.id}`);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PagoHistorialRow({
  pago,
  detail,
  pagoMoneda,
  montoPagado,
  retenido,
  showRetenciones,
  isOpen,
  onToggle,
}: {
  pago: CompraDetailResponse['pagos'][number];
  detail: CompraDetailResponse;
  pagoMoneda: 'ARS' | 'MXN' | 'USD';
  montoPagado: number;
  retenido: number;
  /** Drives the visibility of the Retenciones column / cell — false on
   *  countries without the retenciones feature so MX rows don't show
   *  a stray "$0,00 ARS" column. */
  showRetenciones: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const compraId = detail.header.id;
  const instrumentos = detail.instrumentos.filter((i) => i.fields.IDPago_IP === pago.id);
  const retenciones = detail.retenciones.filter((r) => r.fields.IDPago_RP === pago.id);
  // FileLink/FileUploader writes happen out-of-band against
  // /api/documents — re-fetch the detail so the just-uploaded
  // ComprobanteDriveItemID_IP propagates back into the row.
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['compras', 'detalle', compraId] });
  return (
    <>
      <tr className="hover:bg-muted/20">
        <Td className="text-muted-foreground">{pago.fields.FechaPago_PC || '—'}</Td>
        <Td className="text-foreground">
          {pago.fields.Observaciones_PC || pago.fields.Descripcion_PC || '—'}
        </Td>
        <Td className="text-right font-semibold text-foreground">
          {formatMoney(montoPagado, pagoMoneda)}
        </Td>
        {showRetenciones && (
          <Td className="text-right text-muted-foreground">
            {retenido > 0 ? formatMoney(retenido, 'ARS') : formatMoney(0, 'ARS')}
          </Td>
        )}
        <Td className="text-right">
          <IconAction
            onClick={onToggle}
            title={isOpen ? 'Ocultar detalle' : 'Ver detalle'}
            tone="neutral"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </IconAction>
        </Td>
      </tr>
      {isOpen && (
        <tr className="bg-muted/10">
          <td colSpan={showRetenciones ? 5 : 4} className="px-3 py-3">
            <div
              className={cn(
                'grid gap-3',
                showRetenciones && 'md:grid-cols-2',
              )}
            >
              {/* `min-w-0` on each grid item — without it CSS Grid's
                  default `min-width: auto` lets a long filename inside
                  the card push the column wider than its 1fr share. */}
              <div className="min-w-0 rounded-lg border bg-background">
                <div className="border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Instrumentos
                </div>
                {instrumentos.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Sin instrumentos.</p>
                ) : (
                  <ul className="divide-y text-sm">
                    {instrumentos.map((i) => {
                      const driveId = i.fields.ComprobanteDriveItemID_IP;
                      const fileName = i.fields.ComprobanteFileName_IP;
                      return (
                        <li
                          key={i.id}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-foreground">
                              <CajaNombre caja={i.fields.Cuenta_IP} soloMarca />
                            </span>
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({i.fields.TipoCuenta_IP})
                            </span>
                          </span>
                          <span className="font-semibold">
                            {formatMoney(parseMoney(i.fields.Importe_IP) ?? 0, pagoMoneda)}
                          </span>
                          {/* Always a full-row block under the cuenta+
                              importe line. Using `basis-full` alone left
                              the span sized by its content, so a long
                              filename leaked past the card; `w-full
                              min-w-0` properly clamps it to the li
                              width and lets FileLink's truncate engage. */}
                          <div className="w-full min-w-0">
                            {driveId && fileName ? (
                              // Read-only inside Historial — that
                              // dialog is purely a viewer; uploads /
                              // deletes happen from the Ver detail
                              // page where the user has the full
                              // editor and a clearer mental model
                              // ("estoy editando esta compra").
                              <FileLink
                                entityType="Instrumento"
                                entityId={i.id}
                                fileName={fileName}
                                allowDelete={false}
                                compact
                              />
                            ) : (
                              <FileUploader
                                entityType="Instrumento"
                                entityId={i.id}
                                compact
                                onUploaded={refresh}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {showRetenciones && (
                <div className="rounded-lg border bg-background">
                  <div className="border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Retenciones
                  </div>
                  {retenciones.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">Sin retenciones.</p>
                  ) : (
                    <ul className="divide-y text-sm">
                      {retenciones.map((r) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between px-3 py-1.5"
                        >
                          <span>
                            <span className="font-medium text-foreground">
                              {r.fields.Tipo_RP}
                            </span>
                            <span className="ml-2 text-xs text-muted-foreground">
                              {r.fields.Concepto_RP}
                            </span>
                          </span>
                          <span className="font-semibold">
                            {formatMoney(parseMoney(r.fields.ImporteRetenido_RP) ?? 0, 'ARS')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Pseudo-payment row for a pending Nota de Crédito linked to the
 * current compra. Matches the PowerApps Historial de Pagos pattern
 * (Screen_Administracion line 453) where NCs appear inline alongside
 * real pagos.
 */
function NCHistorialRow({
  nc,
  moneda,
  onVer,
}: {
  nc: CompraDetailResponse['notasCreditoPendientes'][number];
  moneda: 'ARS' | 'MXN' | 'USD';
  onVer: () => void;
}) {
  const monto = parseMoney(nc.fields.Total_CO) ?? 0;
  const numero = nc.fields.NumeroComprobante_CO
    ? `Nº ${nc.fields.NumeroComprobante_CO}`
    : `#${nc.id}`;
  return (
    <tr className="bg-violet-50/40 hover:bg-violet-50/60">
      <Td className="text-muted-foreground">{nc.fields.FechaComprobante_CO || '—'}</Td>
      <Td className="text-foreground">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex rounded-md bg-violet-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800">
            NC
          </span>
          <span>Nota de Crédito {numero}</span>
        </span>
      </Td>
      <Td className="text-right font-semibold text-foreground">
        {formatMoney(monto, moneda)}
      </Td>
      <Td className="text-right text-muted-foreground">—</Td>
      <Td className="text-right">
        <IconAction onClick={onVer} title="Ver Nota de Crédito" tone="neutral">
          <Eye className="h-4 w-4" aria-hidden />
        </IconAction>
      </Td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Cell primitives
// ---------------------------------------------------------------------------

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[140px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function IconAction({
  children,
  onClick,
  title,
  tone,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  title?: string;
  tone: 'neutral' | 'destructive';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
        tone === 'destructive'
          ? 'border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20'
          : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
